import React, { type ReactElement, useState } from 'react';
import Loading from 'atom-components/loading';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import { firstTimeInYear, lastTimeInYear } from 'helpers/year.helpers';
import { organisationUsersWithMarkerRoleParams } from 'pages/marking/helpers/request-params.helpers';
import AllocateResponsesTableContainer from 'pages/marking/subpages/marking-allocation-tab/allocate-responses-modal/allocate-responses-table-container';
import FilterForm from 'pages/marking/subpages/marking-allocation-tab/allocate-responses-modal/filter-form';
import { type FilterFormValues } from 'pages/marking/subpages/marking-allocation-tab/allocate-responses-modal/filter-form/filter-form.schema';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { BaseOrganisation } from 'services/assist-api/entities/customers/customers';
import { useMarkingTasks } from 'services/assist-api/entities/test-instance-response-marker/marking-tasks/hooks';
import { isInitialLoad } from 'utils/check-query-status';

type Props = {
  selectedResponseIds: number[];
  currentOrganisation: BaseOrganisation;
  onClose: () => void;
  onRequestRefetch: () => void;
};

export default function FiltersAndTable({
  selectedResponseIds,
  currentOrganisation,
  onClose,
  onRequestRefetch
}: Props): ReactElement {
  // Here we're storing the filtered marker organisation IDs in state, rather than in the URL, since
  // we don't want to make this modal bookmarkable and we want to preserve the Marking Allocation
  // tab filters so that they can be reused if the modal is closed.
  const [filteredOrganisationUserIds, setFilteredOrganisationUserIds] = useState<number[]>([]);

  // Get filtered markers for these responses (including markers that are already assigned)
  const organisationUsersQuery = useCustomerUsers(
    organisationUsersWithMarkerRoleParams(currentOrganisation?.id, filteredOrganisationUserIds)
  );

  // Get the marking tasks for the selected organisation users:
  const organisationUserIds =
    organisationUsersQuery.data?.json?.map((ou: BaseCustomerUser) => ou.id) || [];

  const currentYear = new Date().getFullYear().toString();
  const markingTasksQuery = useMarkingTasks(
    {
      filters: {
        user: organisationUserIds,
        'testInstanceResponse.testInstance.customer': currentOrganisation.id,
        'testInstanceResponse.testInstance.startDate[after]': firstTimeInYear(currentYear),
        'testInstanceResponse.testInstance.startDate[before]': lastTimeInYear(currentYear)
      }
    },
    { enabled: !!organisationUserIds.length && !!currentOrganisation.id }
  );

  const isLoading = isInitialLoad(organisationUsersQuery, markingTasksQuery);
  const isError = organisationUsersQuery.isError || markingTasksQuery.isError;

  return (
    <div>
      <FilterForm
        onSubmit={(formValues: FilterFormValues): void => {
          setFilteredOrganisationUserIds([...formValues.markerOrganisationUserIds]);
        }}
        onReset={(): void => {
          setFilteredOrganisationUserIds([]);
        }}
      />
      {isLoading && <Loading overlay={false} />}
      {!isLoading && isError && (
        <LoadingErrorAlert queries={[organisationUsersQuery, markingTasksQuery]} />
      )}
      {!isLoading && !isError && (
        <AllocateResponsesTableContainer
          selectedResponseIds={selectedResponseIds}
          organisationUsers={organisationUsersQuery.data?.json || []}
          markingTasks={markingTasksQuery.data?.json || []}
          onClose={onClose}
          onRequestRefetch={onRequestRefetch}
        />
      )}
    </div>
  );
}
